/**
 * Rate-limited prompts, as pure rules over the persisted state:
 *  - Flow G web-to-app (OVL-05): mobile web only, after level 10, again after 3 days, at most
 *    3 times; nothing is ever locked behind it. Desktop only gets a QR code in settings.
 *  - The Google Play banner of the same flow: Android mobile web only, a slim dismissible strip, on the
 *    same cadence (3 days apart, at most 3 times, counted apart) and never within the modal's gap.
 *  - MO-03.2 Starter Pack (OVL-06): once, after the first shop is complete, while the
 *    server's 72-hour offer runs; dismissable, and it stays in the store.
 *  - MT-06.2 streak milestones are celebrated once each.
 *
 * It also owns the links that leave the game for a store or a policy page, since Flow G is the
 * prompt that sends a player to the store listing and the two policy pages are the same settings.
 */
import identity from '../../../mobile/identity.json';
import { cityShops } from '../config/content.js';
import { highestCompleted, isShopComplete } from './progress.js';
import type { PlayerState } from './state.js';

export const WEB_TO_APP = { afterLevel: 10, gapMs: 3 * 86_400_000, max: 3 } as const;

/** The Play banner keeps the modal's cadence (WEB_TO_APP.gapMs) and has its own count. */
export const WEB_BANNER = { gapMs: WEB_TO_APP.gapMs, max: 3 } as const;

export type DeviceClass = 'mobile' | 'desktop' | 'native';

/** Mobile web vs desktop, from the UA and pointer (native builds never ask). */
export function deviceClass(ua: string, coarsePointer: boolean, native: boolean): DeviceClass {
  if (native) return 'native';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (coarsePointer && /Macintosh/.test(ua))) return 'mobile';
  return 'desktop';
}

export function webToAppDue(s: PlayerState, device: DeviceClass, now: number): boolean {
  if (device !== 'mobile') return false;
  if (highestCompleted(s) < WEB_TO_APP.afterLevel) return false;
  const p = s.prompts.webToApp;
  if (p.count >= WEB_TO_APP.max) return false;
  return p.lastAt === null || now - p.lastAt >= WEB_TO_APP.gapMs;
}

/**
 * The Play banner on Android web. Not tied to level 10, because it is a strip and not a modal, but it
 * keeps out of the modal's way: not within the modal's 3-day gap after it showed, and not once the
 * modal has used its three asks (a player who has been invited three times has heard enough).
 */
export function webBannerDue(s: PlayerState, device: DeviceClass, ua: string, now: number): boolean {
  if (device !== 'mobile' || !/Android/i.test(ua)) return false;
  const b = s.prompts.webToAppBanner;
  if (b.count >= WEB_BANNER.max || s.prompts.webToApp.count >= WEB_TO_APP.max) return false;
  const last = Math.max(b.lastAt ?? -Infinity, s.prompts.webToApp.lastAt ?? -Infinity);
  return now - last >= WEB_BANNER.gapMs;
}

export function markWebBanner(s: PlayerState, now: number): void {
  s.prompts.webToAppBanner = { count: s.prompts.webToAppBanner.count + 1, lastAt: now };
}

/**
 * The Play listing from the banner, with the install source the Play referrer will carry back
 * (`installReferrer` in guest auth, services/installReferrer.ts): `utm_source=web&utm_medium=banner`
 * plus the page's own campaign, so a paid visit that installs is still attributed to it.
 */
export function playBannerUrl(campaign: string | null): string {
  const referrer = `utm_source=web&utm_medium=banner${campaign ? `&utm_campaign=${encodeURIComponent(campaign)}` : ''}`;
  return `${storeUrl('Android')}&referrer=${encodeURIComponent(referrer)}`;
}

export function markWebToApp(s: PlayerState, now: number): void {
  s.prompts.webToApp = { count: s.prompts.webToApp.count + 1, lastAt: now };
}

/** The first shop of the city is complete (the server opens the starter offer then). */
export function firstShopComplete(s: PlayerState): boolean {
  const first = cityShops(s.city)[0];
  return !!first && isShopComplete(s, first);
}

export function starterOfferDue(s: PlayerState, offerEndsAt: number | null, now: number): boolean {
  return firstShopComplete(s) && !s.payer && s.prompts.starterShownAt === null && offerEndsAt !== null && offerEndsAt > now;
}

/** A streak milestone reached and not yet celebrated. */
export function streakMilestoneDue(s: PlayerState, milestone: number | null): boolean {
  return milestone !== null && !s.prompts.streakSeen.includes(milestone);
}

/**
 * The numeric App Store id of the iOS listing (`VITE_APPSTORE_ID`). It exists only once the app
 * record has been created in App Store Connect, which is why it is a release setting and not a
 * constant: the same bundle is built before and after the listing goes up. Anything that is not
 * digits is treated as unset rather than built into a broken link.
 */
function appStoreId(env: Readonly<Record<string, unknown>>): string | null {
  const v = env.VITE_APPSTORE_ID;
  return typeof v === 'string' && /^\d+$/.test(v.trim()) ? v.trim() : null;
}

/**
 * Store listing of the native app, for the Flow G web-to-app prompt. The Play URL is the bundle
 * id from `apps/mobile/identity.json` — the one file the apps' identity lives in, which the
 * Capacitor config, the native projects and Fastlane all read, so the link cannot drift from the
 * app that is actually published. Apple has no such id in the URL: it wants the listing's numeric
 * id, so without that setting an iOS visitor gets the App Store's search for the app name, which
 * is the best a build made before the listing existed can do.
 */
export function storeUrl(ua: string, env: Readonly<Record<string, unknown>> = import.meta.env): string {
  if (!/iPhone|iPad|iPod|Macintosh/.test(ua)) return `https://play.google.com/store/apps/details?id=${identity.appId}`;
  const id = appStoreId(env);
  return id ? `https://apps.apple.com/app/id${id}` : `https://apps.apple.com/search?term=${encodeURIComponent(identity.appName)}`;
}

/**
 * The policy pages the store screen has to link next to its purchase buttons (App Store Review
 * 3.1.2 and Play's payments policy both ask for them, and both stores reject a build that only
 * carries the text). They are settings because the pages are served by the help build, whose
 * origin differs per environment; a build with neither set simply shows no links instead of
 * sending a player to a 404.
 */
export function legalUrls(env: Readonly<Record<string, unknown>> = import.meta.env): { privacy: string | null; terms: string | null } {
  const url = (key: string) => {
    const v = env[key];
    return typeof v === 'string' && /^https?:\/\//.test(v.trim()) ? v.trim() : null;
  };
  return { privacy: url('VITE_PRIVACY_URL'), terms: url('VITE_TERMS_URL') };
}
